/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {TabList} from "@mui/lab";
import TabPanel from "@mui/lab/TabPanel/TabPanel";
import Plot from "react-plotly.js";
import TabContext from "@mui/lab/TabContext/TabContext";
import * as React from "react";
import {useState} from "react";
import styled from "styled-components";
import * as colors from "../../../../../../constants/colors";
import { IconButton, Tooltip} from '@mui/material';
import {Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import { MaterialReactTable } from "material-react-table";
import {roundToXDecimals} from "../../../../../../helpers/helpers";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import { fovAndBoresightUltrasonicKeyToLabelMap, COLUMN_NAMES } from "../../constants";
import {ArgosDownloadIcon} from "../../../../../components/Icons/ArgosActionIcons";
import {downloadSensorCsv} from "../../../../../../helpers/sensors/helpers";
import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";
import {getMaxFinite} from "../../../../../../helpers/graphs";

export const RightPanelPlot = (props) => {
  const [globalMin, setGlobalMin] = useState(null);
  const [globalMax, setGlobalMax] = useState(null);
  const [graphOrTable, setGraphOrTable] = useState(0);

  const data = props?.data?.data
  const x = data?.x.map(element => element < 0 ? element + 360 : element)
  const y = data?.y.map(element => element < 0 ? element + 360 : element)
  const columns = props?.data?.columns
  const xLabel = columns?.xLabel ?? ""
  const yLabel = columns?.yLabel ?? ""
  const [tableData, setTableData] = useState([]);
  const [fovAndBoresightUltrasonicTableData, setFovAndBoresightUltrasonicTableData] = useState([]);

  React.useEffect(() => {mapFovAndBoresightUltrasonicValuesToTableFormat()}, [props])
  const mapFovAndBoresightUltrasonicValuesToTableFormat = () => {
    const propValues = props?.fovAndBoresightUltrasonicValues
    let tableRows = []
    Object.keys(propValues).forEach((propKey) => {
      const keyLabelUnitObject = fovAndBoresightUltrasonicKeyToLabelMap.filter((listElement) => listElement.key == propKey)[0]
      const fovAndBoresightUltrasonicTableRow = {
        'propertyName': keyLabelUnitObject.label,
        'propertyValue': propValues[propKey],
        'unit': keyLabelUnitObject.unit
      }
      tableRows.push(fovAndBoresightUltrasonicTableRow)
    })
    setFovAndBoresightUltrasonicTableData(tableRows)
  }

  const getPlotRange = (absoluteMin, absoluteMax) => {
    if (globalMin == null || globalMin > absoluteMin) {
      setGlobalMin(absoluteMin)
    }
    if (globalMax == null || globalMax < absoluteMax) {
      setGlobalMax(absoluteMax)
    }
  }
  React.useEffect(()=> {
    let _x = x.map(element => Math.abs(element))
    let _y = y.map(element => Math.abs(element))
    if (_x != null && _y != null) {
      let absoluteMax = getMaxFinite(_x.concat(_y));
      getPlotRange((-1) * absoluteMax, absoluteMax)
    }
  }, [x, y])

  function handleGraphOrTableChange(e, newValue) {
    setGraphOrTable(newValue)
  }

  React.useEffect(()=> {
    constructTableData();
  }, [props])

  const plotDataColumns = [
    {
      id: 'x',
      accessorFn: (row) => roundToXDecimals(row.x, 3) ?? '',
      header: columns?.xLabel ?? COLUMN_NAMES.AZIMUTH_ANGLE,
      enableEditing: false,
      size: 300,
    },
    {
      id: 'y',
      accessorFn: (row) =>  roundToXDecimals(row.y, 3) ?? '',
      header: columns?.yLabel ?? COLUMN_NAMES.DISTANCE,
      enableEditing: false,
      size: 300,
    }
  ]

  const constructTableData = () => {
    const _tableData = []
    for (let i = 0; i < x.length; i++){
      let newObject = {
        "x" : x[i],
        "y" : y[i],
      }
      _tableData.push(newObject)
    }
    setTableData(_tableData)
  }

  const fovAndBoresightUltrasonicTableColumns = [
    {
      header: "Ultrasonic Properties",
      accessorKey: "propertyName",
      enableEditing: false,
      size: 250,
    },
    {
      id: 'propertyValue',
      accessorFn: (row) => roundToXDecimals(row.propertyValue, 1) ?? '',
      header: "Value",
      enableEditing: true,
      size: 220,
    },
    {
      header: "Unit",
      accessorKey: "unit",
      enableEditing: false,
    },
  ]
  /**
   * Top Toolbar Actions for the Ultrasonic Performance Table
   * This component renders the actions available in the top toolbar of the Ultrasonic Performance Table.
   * It includes a download button to export the table data as a CSV file.
   * @returns {JSX.Element}
   */
  const actions = (
    /* Top Toolbar Actions*/
    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <Tooltip title="Download CSV" arrow>
        <IconButton>
          <ArgosDownloadIcon onClick={() => downloadSensorCsv(tableData, `${document.title} Performance Data`, COLUMN_NAMES.AZIMUTH_ANGLE, COLUMN_NAMES.DISTANCE)}/>
        </IconButton>
      </Tooltip>
    </div>
  );
  return (
    <div style={{display: 'flex', flexFlow: 'row nowrap', overflow: 'auto', height: '100%', alignItems: 'center'}}>
      <TabContext value={graphOrTable}>
        <ArgosFlexbox vertical>
        <div>
          <TabList value={graphOrTable} onChange={handleGraphOrTableChange} aria-label="basic tabs example">
            <Tab sx={{fontSize: 12}} label="Graph" value={0}/>
            <Tab sx={{fontSize: 12}} label="Data Table" value={1}/>
          </TabList>
        </div>
        <div style={{flexGrow: 1, overflow: 'auto'}}>
          <TabPanel style={{height: "100%"}} value={0} index={0}>
            <Plot
              data={[{
                r: y,
                theta: x,
                type: 'scatterpolar',
                mode: 'markers',
                line: {'shape': 'spline', 'smoothing': 1.3}
              }]}
              useResizeHandler={true}
              style={{width: 800, height: 800}}
              layout={{
                paper_bgcolor: '#f2f4f5',
                plot_bgcolor: '#f2f4f5',
                title: 'Ultrasonic Performance',
                shapes: {type: 'path'},
                autosize: true,
                polar: {
                  angularaxis: {
                    rotation: 90
                  }
                },
                xaxis: {title: xLabel, range: [globalMin, globalMax], nticks: 6, rangemode: "tozero"},
                yaxis: {title: yLabel, range: [globalMin, globalMax], nticks: 6, rangemode: "tozero"},
              }}
            />

          </TabPanel>
          <TabPanel value={1} index={1} style={{overflow: 'auto', height: '100%',}}>
            <div style={{padding: "5px"}}>
              <ArgosTableSimple
                columns={plotDataColumns}
                data={tableData}
                renderTopToolbarCustomActions={() => actions}
                initialState={{density: 'compact'}}
                enableRowActions
                enableEditing
                editingMode="cell"
                enablePagination={true}
                enableBottomToolbar={true}
                muiTableProps={{
                  sx: {
                    tableLayout: 'fixed',
                    width: "800px",
                  },
                }}
              />
          </div>
          </TabPanel>
        </div>
        </ArgosFlexbox>
      </TabContext>
      <div style={{paddingRight: '10px', height: '50%', width: '100%'}}>
        <ArgosTableSimple
          columns={fovAndBoresightUltrasonicTableColumns}
          data={fovAndBoresightUltrasonicTableData}

          initialState={{density: 'compact'}}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          enablePagination={false}
        />
      </div>
    </div>
  )
}
